// src/pages/Payments/PaymentEdit.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { usePayment } from '../../hooks/usePayment';
import { usePayments } from '../../hooks/usePayments';

const CATEGORIES = ['Registration Fee', 'Materials', 'Penalty', 'Misc'];

const PaymentEdit = () => {
  const { id } = useParams(); // payment id
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromStudent = searchParams.get('fromStudent'); // student id if navigated from Student details

  // Single payment
  const { payment, loading, error } = usePayment(id);

  // Hook bundle (mutations + extra readers)
  const {
    updatePayment,
    useOtherCharges,
    addOtherChargesBulk,
    deleteOtherCharge,   // expects { id, paymentId? }
    updateOtherCharge,   // expects { id, patch, paymentId? }
    usePaymentTotals,
  } = usePayments();

  // Load other charges via hook
  const {
    data: otherCharges = [],
    isLoading: ocLoading,
    error: ocError,
  } = useOtherCharges(id);

  // Payment totals from reconciliation view (safe if view is missing -> null)
  const { data: totals } = usePaymentTotals(id);

  // ---- Local editable fields ----
  const [form, setForm] = useState({
    amount: '',
    payment_method: '',
    payment_date: '',
    status: '',
    notes: '',
    invoice_number: '',
  });

  // Keep an original snapshot to detect unsaved changes
  const initialFormRef = useRef(null);

  // Inline-edit map for existing rows: { [id]: { category, description, amount } }
  const [editRows, setEditRows] = useState({});
  const [savingRowId, setSavingRowId] = useState(null);

  // Staged "new" other charge rows (to be inserted on Save Changes)
  const [newCharges, setNewCharges] = useState([]);

  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  // Seed form from fetched payment
  useEffect(() => {
    if (!payment) return;
    const seed = {
      amount: payment.amount ?? '',
      payment_method: payment.payment_method ?? '',
      payment_date: payment.payment_date
        ? new Date(payment.payment_date).toISOString().split('T')[0]
        : '',
      status: payment.status ?? 'Pending',
      notes: payment.notes ?? '',
      invoice_number: payment.invoice_number ?? '',
    };
    setForm(seed);
    initialFormRef.current = seed;
  }, [payment]);

  const backToDetailsHref = fromStudent ? `/payments/${id}?fromStudent=${fromStudent}` : `/payments/${id}`;

  const prettyError = (e) => {
    const msg = e?.message || String(e);
    if (/permission denied/i.test(msg)) return 'You do not have permission to perform this action.';
    if (/duplicate key|payments_invoice_number_key/i.test(msg)) return 'Invoice number already exists.';
    return msg;
  };

  /* ===================== Inline edit helpers (existing rows) ===================== */
  const startEditRow = (oc) => {
    setEditRows((m) => ({
      ...m,
      [oc.id]: {
        category: oc.category ?? '',
        description: oc.description ?? '',
        amount: String(oc.amount ?? ''),
      },
    }));
  };

  const cancelEditRow = (rowId) => {
    setEditRows((m) => {
      const copy = { ...m };
      delete copy[rowId];
      return copy;
    });
  };

  const updateEditField = (rowId, field, value) => {
    setEditRows((m) => ({
      ...m,
      [rowId]: { ...(m[rowId] || {}), [field]: value },
    }));
  };

  const validateEditRow = (row) => {
    const amt = Number(row.amount);
    return !!row.category && !!row.description && Number.isFinite(amt) && amt > 0;
  };

  const handleSaveRow = async (rowId) => {
    try {
      setSaveError('');
      const row = editRows[rowId];
      if (!row) return;

      if (!validateEditRow(row)) {
        throw new Error('Please complete the row with a valid amount > 0.');
      }

      setSavingRowId(rowId);
      await updateOtherCharge({
        id: rowId,
        patch: {
          category: row.category,
          description: row.description,
          amount: Number(row.amount || 0),
        },
        paymentId: id, // helps the hook invalidate the right queries
      });

      // exit edit mode on success
      cancelEditRow(rowId);
    } catch (e) {
      setSaveError(prettyError(e));
    } finally {
      setSavingRowId(null);
    }
  };

  /* ===================== Staged NEW charges (insert on Save) ===================== */
  const addNewChargeRow = () => {
    setNewCharges((prev) => [
      ...prev,
      { id: `local-${crypto.randomUUID?.() || Date.now()}`, category: '', description: '', amount: '' },
    ]);
  };
  const removeNewChargeRow = (rid) => setNewCharges((prev) => prev.filter((r) => r.id !== rid));
  const updateNewChargeField = (rid, field, value) =>
    setNewCharges((prev) => prev.map((r) => (r.id === rid ? { ...r, [field]: value } : r)));

  const validateNewCharges = () => {
    for (const r of newCharges) {
      const amt = Number(r.amount);
      if (!r.category || !r.description || !Number.isFinite(amt) || !(amt > 0)) {
        setSaveError('Please complete all new Other Charges rows with a valid amount > 0.');
        return false;
      }
    }
    return true;
  };

  /* ===================== Totals preview (includes staged edits) ===================== */
  // Enrollments total: from view if present; fallback to UI sum of payment.enrollments
  const enrollmentsTotal = useMemo(() => {
    if (totals?.enrollments_total != null) return Number(totals.enrollments_total);
    if (Array.isArray(payment?.enrollments)) {
      return payment.enrollments.reduce((s, e) => s + Number(e.amount_applied || 0), 0);
    }
    return 0;
  }, [totals, payment]);

  const existingOtherChargesTotal = useMemo(() => {
    return (otherCharges || []).reduce((s, oc) => s + Number(oc.amount || 0), 0);
  }, [otherCharges]);

  const stagedOtherChargesTotal = useMemo(() => {
    return (newCharges || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  }, [newCharges]);

  // Delta from inline edits vs current fetched values (not yet saved)
  const pendingEditsDelta = useMemo(() => {
    let delta = 0;
    for (const oc of otherCharges) {
      const draft = editRows[oc.id];
      if (draft && draft.amount !== undefined && draft.amount !== null && draft.amount !== '') {
        const nextAmt = Number(draft.amount);
        const curAmt = Number(oc.amount || 0);
        if (Number.isFinite(nextAmt)) delta += nextAmt - curAmt;
      }
    }
    return Math.round(delta * 100) / 100;
  }, [editRows, otherCharges]);

  const itemsSubtotalFuture = useMemo(() => {
    const base = enrollmentsTotal + existingOtherChargesTotal;
    return Math.round((base + stagedOtherChargesTotal + pendingEditsDelta) * 100) / 100;
  }, [enrollmentsTotal, existingOtherChargesTotal, stagedOtherChargesTotal, pendingEditsDelta]);

  const amountInput = Number(form.amount || 0);
  const mismatch = useMemo(() => Math.abs(amountInput - itemsSubtotalFuture) > 0.01, [amountInput, itemsSubtotalFuture]);

  /* ===================== Unsaved changes protection ===================== */
  const formDirty = useMemo(() => {
    if (!initialFormRef.current) return false;
    try {
      return JSON.stringify(form) !== JSON.stringify(initialFormRef.current);
    } catch {
      return true;
    }
  }, [form]);

  const isDirty = formDirty || Object.keys(editRows).length > 0 || newCharges.length > 0;

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const safeNavigate = useCallback(
    (to, { skipConfirm = false, replace = false } = {}) => {
       if (!skipConfirm && isDirty && !window.confirm('You have unsaved changes. Leave without saving?')) {
         return;
       }
      navigate(to, { replace });
    },
    [isDirty, navigate]
  );

  const handleCancel = () => {
    if (fromStudent) {
      safeNavigate(`/students/${fromStudent}?tab=payments`, { replace: true });
    } else {
      safeNavigate('/payments', { replace: true });
    }
  };

  /* ===================== Page-level save (basics + stage insert) ===================== */
  const handleSave = async () => {
    try {
      setSaveError('');

      // Always enforce reconciliation (we have a reliable fallback even without the view)
      if (mismatch) {
        throw new Error(
          'Amount (RM) must equal Enrollments total + Other Charges (including inline edits and new rows). ' +
          'Please adjust the Amount or your Other Charges so they reconcile.'
        );
      }

      setSaving(true);

      // 1) Update payment basics
      const patch = {
        amount: Number(form.amount || 0),
        payment_method: form.payment_method || null,
        payment_date: form.payment_date || null,
        status: form.status || 'Pending',
        notes: form.notes || null,
        invoice_number: form.invoice_number || null,
      };
      await updatePayment({ id, patch, student_id: payment?.student_id });

      // 2) Insert staged other charges (if any)
      if (newCharges.length > 0) {
        if (!validateNewCharges()) {
          setSaving(false);
          return;
        }
        await addOtherChargesBulk({
          paymentId: id,
          rows: newCharges.map((r) => ({
            category: r.category,
            description: r.description,
            amount: Number(r.amount || 0),
          })),
        });
        setNewCharges([]); // clear staged rows
      }

      // 3) Navigate back to details (preserving student context if present)
      initialFormRef.current = { ...form }; // current form is the new baseline
      setEditRows({});                      // clear any inline-edit markers
      setNewCharges([]);                    // already done above, but keep for clarity

      
      safeNavigate(backToDetailsHref, { skipConfirm: true, replace: true });  
      
    } catch (e) {
      setSaveError(prettyError(e));
    } finally {
      setSaving(false);
    }
  };


  if (loading) return <div className="text-center py-8">Loading payment…</div>;
  if (error)   return <div className="text-center py-8 text-red-500">Error: {String(error)}</div>;
  if (!payment) return <div className="text-center py-8">Payment not found</div>;

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}

      {/* Error banner */}
      {(saveError || ocError) && (
        <div className="mb-4 p-3 rounded bg-red-100 text-red-700">
          {saveError || String(ocError)}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Edit form + Enrollments + Other Charges */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <h2 className="text-xl font-semibold mb-4">Edit Payment</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Student (read-only) */}
              <div>
                <label className="block text-sm text-gray-500 mb-1">Student</label>
                <div className="border rounded-md px-3 py-2 bg-gray-50">
                  {payment.student_name || payment.student_full_name || '—'}
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <label htmlFor="payment_method" className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
                <select
                  id="payment_method"
                  value={form.payment_method}
                  onChange={(e) => setForm((f) => ({ ...f, payment_method: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select a method</option>
                  <option value="Online Transfer">Online Transfer</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Check">Check</option>
                  <option value="Others">Others</option>
                </select>
              </div>

              {/* Payment Date */}
              <div>
                <label htmlFor="payment_date" className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
                <input
                  id="payment_date"
                  type="date"
                  value={form.payment_date}
                  onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              {/* Status */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                <select
                  id="status"
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="Pending">Pending</option>
                  <option value="Paid">Paid</option>
                  <option value="Refunded">Refunded</option>
                </select>
              </div>

              {/* Invoice Number */}
              <div className="md:col-span-2">
                <label htmlFor="invoice_number" className="block text-sm font-medium text-gray-700 mb-1">Invoice Number *</label>
                <input
                  id="invoice_number"
                  type="text"
                  value={form.invoice_number}
                  onChange={(e) => setForm((f) => ({ ...f, invoice_number: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                  placeholder="Enter invoice number"
                />
              </div>

              {/* Amount */}
              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">Amount (RM) *</label>
                <input
                  id="amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  This should equal <b>Enrollments total + Other Charges</b>.
                </p>
                <p className={`mt-1 text-xs ${mismatch ? 'text-red-600' : 'text-green-700'}`}>
                  {mismatch
                    ? `Mismatch: After save, items subtotal would be RM${itemsSubtotalFuture.toFixed(2)}`
                    : `OK: After save, items subtotal will match RM${itemsSubtotalFuture.toFixed(2)}`}
                </p>
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  id="notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
          </Card>

          {/* Enrollments (read-only) */}
          <Card>
            <h2 className="text-xl font-semibold mb-4">Enrollments Covered</h2>
            {Array.isArray(payment.enrollments) && payment.enrollments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Intake</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount Applied</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payment.enrollments.map((enr, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 text-sm">{enr.subject_name}</td>
                        <td className="px-6 py-4 text-sm">{enr.intake_name}</td>
                        <td className="px-6 py-4 text-sm">
                          RM{Number(enr.amount_applied).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500">No enrollments linked to this payment.</p>
            )}
          </Card>

          {/* Other Charges */}
          <Card>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-semibold">Other Charges</h2>
              <Button onClick={addNewChargeRow}>+ Add Charge</Button>
            </div>

            {/* Existing charges list */}
            {ocLoading ? (
              <div className="py-4 text-gray-600">Loading other charges…</div>
            ) : (otherCharges?.length > 0 ? (
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount (RM)</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {otherCharges.map((oc) => {
                      const isEditing = !!editRows[oc.id];
                      const draft = editRows[oc.id];

                      return (
                        <tr key={oc.id}>
                          <td className="px-3 py-2 text-sm">
                            {isEditing ? (
                              <select
                                value={draft.category}
                                onChange={(e) => updateEditField(oc.id, 'category', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                                disabled={savingRowId === oc.id}
                              >
                                <option value="">Select</option>
                                {CATEGORIES.map((c) => (
                                  <option key={c} value={c}>{c}</option>
                                ))}
                              </select>
                            ) : (
                              oc.category
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm">
                            {isEditing ? (
                              <input
                                type="text"
                                value={draft.description}
                                onChange={(e) => updateEditField(oc.id, 'description', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                                disabled={savingRowId === oc.id}
                              />
                            ) : (
                              oc.description
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm">
                            {isEditing ? (
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={draft.amount}
                                onChange={(e) => updateEditField(oc.id, 'amount', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                                disabled={savingRowId === oc.id}
                              />
                            ) : (
                              <>RM{Number(oc.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {!isEditing ? (
                              <div className="inline-flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => startEditRow(oc)}
                                  className="px-2 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteOtherCharge({ id: oc.id, paymentId: id })}
                                  className="px-2 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                                >
                                  Delete
                                </button>
                              </div>
                            ) : (
                              <div className="inline-flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleSaveRow(oc.id)}
                                  className="px-2 py-1 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm disabled:opacity-50"
                                  disabled={savingRowId === oc.id}
                                >
                                  {savingRowId === oc.id ? 'Saving…' : 'Save'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => cancelEditRow(oc.id)}
                                  className="px-2 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                                  disabled={savingRowId === oc.id}
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50">
                      <td className="px-3 py-2 text-right font-medium" colSpan={2}>Existing Other Charges Total</td>
                      <td className="px-3 py-2 font-semibold">
                        RM{existingOtherChargesTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 mb-4">No other charges yet.</p>
            ))}

            {/* Staged new charges (to insert on Save Changes) */}
            {newCharges.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount (RM)</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {newCharges.map((row) => (
                      <tr key={row.id}>
                        <td className="px-3 py-2">
                          <select
                            value={row.category}
                            onChange={(e) => updateNewChargeField(row.id, 'category', e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                          >
                            <option value="">Select</option>
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={row.description}
                            onChange={(e) => updateNewChargeField(row.id, 'description', e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                            placeholder="Describe the charge"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.amount}
                            onChange={(e) => updateNewChargeField(row.id, 'amount', e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => removeNewChargeRow(row.id)}
                            className="px-2 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50">
                      <td className="px-3 py-2 text-right font-medium" colSpan={2}>Staged Subtotal</td>
                      <td className="px-3 py-2 font-semibold">
                        RM{stagedOtherChargesTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
                <p className="mt-2 text-xs text-gray-500">
                  These will be inserted when you click <b>Save Changes</b>.
                </p>
              </div>
            )}
          </Card>
        </div>

        {/* Right: Totals panel + Actions */}
        <div className="space-y-6">
          <Card>
            <h2 className="text-xl font-semibold mb-3">Totals</h2>
            <div className="text-sm space-y-2">
              <div className="flex justify-between">
                <span>Enrollments total</span>
                <span>RM{enrollmentsTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between">
                <span>Existing Other Charges</span>
                <span>RM{existingOtherChargesTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between">
                <span>+ Pending inline edits</span>
                <span>RM{pendingEditsDelta.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between">
                <span>+ Staged (pending add)</span>
                <span>RM{stagedOtherChargesTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <hr className="my-2" />
              <div className="flex justify-between font-semibold">
                <span>Items subtotal (after save/inline)</span>
                <span>RM{itemsSubtotalFuture.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between">
                <span>Payment Amount (edited)</span>
                <span>RM{amountInput.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              {mismatch ? (
                <div className="mt-2 p-2 rounded bg-red-50 text-red-700">
                  Amount will not match items subtotal. Adjust Amount or Other Charges.
                </div>
              ) : (
                <div className="mt-2 p-2 rounded bg-green-50 text-green-700">
                  Amount matches items subtotal.
                </div>
              )}
              {totals ? (
                <p className="mt-2 text-xs text-gray-500">Totals powered by <code>v_payment_totals</code>.</p>
              ) : (
                <p className="mt-2 text-xs text-gray-500">Totals computed in UI (create the <code>v_payment_totals</code> view for server-side reconciliation).</p>
              )}
            </div>
          </Card>

          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancel} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1 disabled:opacity-50"
              disabled={saving || savingRowId !== null || mismatch}
              title={mismatch ? 'Fix totals mismatch before saving' : ''}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentEdit;
